import {
  StatsigOptions,
  StatsigProvider as BaseStatsigProvider,
} from "@statsig/react-bindings";
import { StatsigClient, StatsigUser } from "@statsig/js-client";
import { getEnvFromHostname } from "@growtherapy/analytics";
import { getCurrentHost } from "../utils";
import { datadogRum } from "@datadog/browser-rum";
import { createContext } from "react";
import { ChromeToolbarPlaybackControlsSingleton } from "../classes/ChromeToolbarPlaybackControlsSingleton";
import { logger } from "../datadog/logger";
import { LoadingScreen } from "../components/LoadingScreen";
import { useAsync } from "react-use";

export const StatsigLoadingContext = createContext<boolean>(false);
type StatsigProviderOptions = StatsigOptions & {
  gateEvaluationCallback?: (key: string, value: boolean) => void;
  api?: string;
};

const statsigOptions: StatsigProviderOptions = {
  environment: {
    tier: getEnvFromHostname({ hostname: getCurrentHost() }),
  },
  // https://docs.datadoghq.com/real_user_monitoring/guide/setup-feature-flag-data-collection/?tab=browser#statsig-integration
  gateEvaluationCallback(key: string, value: boolean) {
    datadogRum.addFeatureFlagEvaluation(key, value);
  },
  // https://docs.statsig.com/infrastructure/managed-proxy/
  api: "https://yjxpzuzkp2367lnlk2xoxcc56q0hrtww.lambda-url.ap-south-1.on.aws/v1",
};

const STATSIG_CLIENT_KEY = "client-y2YXcN60n7VU4EkzyZABzzKCDe6gCuQtb70uLgf3U1u";

export const Statsig = new StatsigClient(
  STATSIG_CLIENT_KEY,
  {},
  statsigOptions,
);

export type StatsigProviderProps = {
  children: React.ReactNode;
  user?: StatsigUser;
};

/**
 * Callback function to be executed after Statsig client initialization is
 * successful.
 *
 * Useful for initializing non-React things that depend on Statsig values.
 */
function onStatsigInitializationSuccess() {
  ChromeToolbarPlaybackControlsSingleton.getInstance();
}

/**
 * Statsig Provider for the application. Initializes the {@link Statsig} client.
 */
export function StatsigProvider({ children, user }: StatsigProviderProps) {
  const { loading } = useAsync(
    async function handleStatsigClientInitialization() {
      try {
        await Statsig.initializeAsync();
        onStatsigInitializationSuccess();
      } catch (error) {
        logger.error("Failed to initialize Statsig client", {}, error as Error);
      }
    },
    [],
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <BaseStatsigProvider client={Statsig} user={user}>
      {children}
    </BaseStatsigProvider>
  );
}
